import "@/components/feedback/feedback_file_link";
import { FileEntry } from "@/types/FeedbackResult";

/**
 * Rewrites relative media anchor hrefs in sanitized HTML strings to the full
 * activity URL. Anchors whose href starts with `media/` or `./media/` are
 * rewritten to `<activityPath>/media/<rest>[?token=<token>]`. Other hrefs
 * are untouched.
 */
export function contextualizeMediaPaths(htmlString: string, activityPath?: string, token?: string | null): string {
    if (!activityPath) return htmlString;
    const tokenPart = token ? `?token=${token}` : "";
    const tpl = document.createElement("template");
    tpl.innerHTML = htmlString;
    tpl.content.querySelectorAll("a[href^='media/'], a[href^='./media/']").forEach((a) => {
        const value = a.getAttribute("href")!;
        const rest = value.startsWith("./media/") ? value.slice(8) : value.slice(6);
        a.setAttribute("href", `${activityPath}/media/${rest}${tokenPart}`);
    });
    return tpl.innerHTML;
}

// Elements in which we do not replace file names with links.
// Elements not included in ALLOWED_TAGS are not denied,
// as they cannot reach this function anyway.
// d-feedback-file-link is added to prevent doubly linking Pythia file links.
const LINKIFY_DENY_LIST = new Set(["a", "svg", "d-feedback-file-link"]);

/**
 * Replace judge-generated `<a class="file-link">` anchors in an HTML
 * fragment with `<d-feedback-file-link>` elements, matching each anchor's text
 * against the file dictionary.
 *
 * This is used for Pythia diff output, where only the judge's explicit anchors
 * should become links; file names in the diff body itself stay literal.
 *
 * @param htmlString The HTML fragment to process.
 * @param files A dictionary of file entries.
 * @param activityPath The path of the activity.
 * @param accessToken The access token for the activity.
 */
export function insertJudgeFileLinks(
    htmlString: string,
    files: Record<string, FileEntry> | null,
    activityPath: string = "",
    accessToken: string | null = null,
): string {
    if (!files || Object.keys(files).length === 0) {
        return htmlString;
    }

    const tpl = document.createElement("template");
    tpl.innerHTML = htmlString;
    replaceJudgeFileLinks(tpl.content, files, activityPath, accessToken);
    return tpl.innerHTML;
}

// Replace the judge-generated `<a class="file-link">` anchors under `root` in place.
function replaceJudgeFileLinks(
    root: DocumentFragment,
    files: Record<string, FileEntry>,
    activityPath: string,
    accessToken: string | null,
): void {
    root.querySelectorAll("a.file-link").forEach((anchor) => {
        const name = anchor.textContent?.trim() ?? "";
        const entry = files[name];
        if (entry) {
            anchor.replaceWith(createFileLink(name, entry, activityPath, accessToken));
        }
    });
}

/**
 * Replace occurrences of the file names in an HTML fragment with a `<d-feedback-file-link>` element pointing at that file.
 *
 * Additionally, this function intercepts the old jugde-generated links at put them into the same file system.
 *
 * @param htmlString The HTML fragment to process.
 * @param files A dictionary of file entries.
 * @param activityPath The path of the activity.
 * @param accessToken The access token for the activity.
 */
export function insertHtmlLinksToFiles(
    htmlString: string,
    files: Record<string, FileEntry> | null,
    activityPath: string = "",
    accessToken: string | null = null,
): string {
    const keys = Object.keys(files ?? {});
    if (!files || keys.length === 0) {
        return htmlString;
    }

    // Handle cases like "file.txt" and "file.txt.zip"
    const sortedKeys = keys.sort((a, b) => b.length - a.length);
    const filenamePattern = sortedKeys.map(RegExp.escape).join("|");
    const pattern = new RegExp(`(?<![\\w.-])(${filenamePattern})(?![\\w.-])`, "g");

    const tpl = document.createElement("template");
    tpl.innerHTML = htmlString;

    // Handle Pythia links
    replaceJudgeFileLinks(tpl.content, files, activityPath, accessToken);

    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            // Pure text child, if reached is in an allowed tag.
            if (node.nodeType === Node.TEXT_NODE) {
                return NodeFilter.FILTER_ACCEPT;
            }

            if (!LINKIFY_DENY_LIST.has((node as Element).tagName.toLowerCase())) {
                // Skip the element itself but descend into children.
                return NodeFilter.FILTER_SKIP;
            } else {
                // Ignore fully.
                return NodeFilter.FILTER_REJECT;
            }
        },
    });

    const textNodes: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        textNodes.push(node as Text);
    }

    for (const node of textNodes) {
        const text = node.textContent ?? "";

        let fragment: DocumentFragment | null = null;
        let lastIndex = 0;
        for (const match of text.matchAll(pattern)) {
            const entry = files[match[0]];
            if (!entry) {
                continue;
            }

            // Create it in the loop the first time we need it.
            fragment ??= document.createDocumentFragment();

            fragment.append(text.slice(lastIndex, match.index));
            fragment.append(createFileLink(match[0], entry, activityPath, accessToken));

            lastIndex = match.index + match[0].length;
        }

        if (fragment) {
            fragment.append(text.slice(lastIndex));
            node.replaceWith(fragment);
        }
    }

    return tpl.innerHTML;
}

function createFileLink(name: string, entry: FileEntry, activityPath: string, accessToken: string | null): HTMLElement {
    // Building the element via the DOM lets the browser handle attribute escaping.
    const link = document.createElement("d-feedback-file-link");
    link.setAttribute("name", name);
    link.setAttribute("location", entry.location);
    link.setAttribute("content", entry.content);
    link.setAttribute("activityPath", activityPath);
    if (accessToken) {
        link.setAttribute("accessToken", accessToken);
    }

    return link;
}
